import { FC, ReactNode, useState } from "react";
import { useBooleanLocalStorage } from "@src/lib/hooks/useLocalStorage";
import { Button } from "@src/lib/ui/Button";
import ReleaseEnvelope from "./ReleaseEnvelope";
import FirstAgentBanner from "./FirstAgentBanner";
import ReleaseHeadlineCard from "./ReleaseHeadlineCard";
import { ReleaseHeadlineRelease } from "./releases";
import useVisibleRelease from "./useVisibleRelease";

const ReleaseHeadline: FC = () => {
  const visible = useVisibleRelease();
  // Previews get a Replay button that remounts the release from the start.
  const [replays, setReplays] = useState(0);
  if (!visible) return null;
  const { release, experience, isPreview } = visible;
  // The nav letter lives in the left nav (AnnouncementLetter); the homepage
  // shows nothing for it.
  if (experience === "nav-letter") return null;

  const key = `${release.slug}-${replays}`;
  const content =
    experience === "envelope" ? (
      <ReleaseEnvelopeExperience
        key={key}
        release={release}
        isPreview={isPreview}
      />
    ) : (
      <ReleaseHeadlineCard
        key={key}
        release={release}
        isPreview={isPreview}
        celebrate
      />
    );
  if (!isPreview) return content;
  return (
    <PreviewReplay onReplay={() => setReplays((count) => count + 1)}>
      {content}
    </PreviewReplay>
  );
};

const PreviewReplay: FC<{ onReplay: () => void; children: ReactNode }> = ({
  onReplay,
  children,
}) => (
  <>
    {children}
    <Button
      variant="outline"
      size="sm"
      className="fixed right-4 bottom-4 z-50 shadow-md"
      onClick={onReplay}
    >
      Replay release preview
    </Button>
  </>
);

// First visit: the envelope pops up and opens by itself. Once it has been
// opened, the homepage shows only a small "add your first agent" banner.
export const ReleaseEnvelopeExperience: FC<{
  release: ReleaseHeadlineRelease;
  isPreview: boolean;
}> = ({ release, isPreview }) => {
  const [storedOpened, setStoredOpened] = useBooleanLocalStorage(
    `opened_release_envelope_${release.slug}`,
    false,
  );
  // Opening the envelope is the celebration, so the card won't replay confetti
  // even if the release later switches to the card experience. Likewise, a
  // viewer who already saw the card (before the envelope flag was turned on)
  // goes straight to the banner instead of getting the envelope.
  const [celebrated, setCelebrated] = useBooleanLocalStorage(
    `celebrated_release_headline_${release.slug}`,
    false,
  );
  // Previews replay the envelope on every load without touching storage.
  const [previewOpened, setPreviewOpened] = useState(false);
  const opened = isPreview ? previewOpened : storedOpened || celebrated;
  const [showingLetter, setShowingLetter] = useState(false);
  const [closedLetter, setClosedLetter] = useState(false);

  if (!opened || showingLetter) {
    return (
      <ReleaseEnvelope
        release={release}
        onOpen={() => {
          setShowingLetter(true);
          if (isPreview) setPreviewOpened(true);
          else {
            setStoredOpened(true);
            setCelebrated(true);
          }
        }}
        onClose={() => {
          setShowingLetter(false);
          setClosedLetter(true);
        }}
      />
    );
  }
  // The letter already made the announcement; the homepage keeps only a
  // small nudge to add a first agent.
  return <FirstAgentBanner isPreview={isPreview} focusOnMount={closedLetter} />;
};

export default ReleaseHeadline;
